import { createApi } from '@reduxjs/toolkit/query/react';

import type { ReferralRequestSchemaType } from '@/global/schemas/price.schema';
import type {
  GetProjectGroupsV1ResponseSchemaType,
  GetProjectWalletV1ResponseSchemaType,
  GetProjectWalletV2ResponseSchemaType,
  ProjectRefundV2SchemaType,
} from '@/global/schemas/project.schema';
import type { GetThemeResponseSchemaType } from '@/global/schemas/theme.schema';

import { REDUX_SLICE } from '../constants';
import { axiosBaseQuery } from '../utils';

export const projectApi = createApi({
  reducerPath: REDUX_SLICE.PROJECT_API,
  tagTypes: [
    'ProjectSGV1',
    'ProjectGroupsV1',
    'ProjectWalletV1',
    'ReferralUpdateV1',
    'ReferralUpdateV2',
    'ProjectThemeV2',
    'ProjectRefundV2',
  ],
  baseQuery: axiosBaseQuery,
  endpoints: builder => ({
    fetchProjectThemeV2: builder.query<GetThemeResponseSchemaType, { nodeId: string }>({
      query: ({ nodeId }) => ({ path: `v1/projects/${nodeId}/themes` }),
      transformResponse: (returnValue: GetThemeResponseSchemaType) => returnValue,
      providesTags: (_result, _error, { nodeId }) => [{ type: 'ProjectThemeV2', id: nodeId }],
    }),
    fetchProjectWalletV1: builder.query<GetProjectWalletV1ResponseSchemaType, { nodeId: string; address: string }>({
      query: ({ nodeId, address }) => ({
        path: `v1/projects/${nodeId}/wallets/${address}`,
        version: 'v1',
      }),
      transformResponse: (returnValue: GetProjectWalletV1ResponseSchemaType) => returnValue,
      providesTags: (_result, _error, { nodeId, address }) => [{ type: 'ProjectWalletV1', id: `${nodeId}_${address}` }],
    }),
    fetchProjectGroupsV1: builder.query<GetProjectGroupsV1ResponseSchemaType, { nodeId: string }>({
      query: ({ nodeId }) => ({
        path: `v1/projects/${nodeId}/groups`,
        version: 'v1',
      }),
      transformResponse: (returnValue: GetProjectGroupsV1ResponseSchemaType) => returnValue,
      providesTags: (_result, _error, { nodeId }) => [{ type: 'ProjectGroupsV1', id: nodeId }],
    }),
    updateReferralWalletV1: builder.mutation<any, ReferralRequestSchemaType>({
      query: data => ({
        method: 'PUT',
        path: `v1/projects/${data.nft}/wallets`,
        data,
        version: 'v1',
      }),
      transformResponse: (returnValue: any) => {
        return returnValue.data ?? { isError: true, errorMessage: returnValue?.message };
      },
      invalidatesTags: (_result, _error) => [{ type: 'ReferralUpdateV1' }],
    }),
    updateReferralWalletV2: builder.mutation<any, ReferralRequestSchemaType>({
      query: data => ({
        method: 'PUT',
        path: `v1/projects/${data.nft}/wallets`,
        data: {
          ...data,
          sale: data.nft,
        },
        headers: {
          'x-offchain-address': data.address,
        },
        version: 'v2',
      }),
      transformResponse: (returnValue: any) => {
        return returnValue.data ?? { isError: true, errorMessage: returnValue?.message };
      },
      invalidatesTags: (_result, _error) => [{ type: 'ReferralUpdateV2' }],
    }),
    fetchInventoriesV2: builder.mutation<any, { address: `0x${string}`; data: { nodes: string[] } }>({
      query: ({ address, data }) => ({
        method: 'POST',
        path: `v1/projects/nfts`,
        data,
        headers: {
          'x-offchain-address': address,
        },
      }),
      transformResponse: (returnValue: any) => {
        return returnValue.data ?? { isError: true, errorMessage: returnValue?.message };
      },
    }),
    fetchProjectRefundV2: builder.query<ProjectRefundV2SchemaType, { nodeId: string; address?: string }>({
      query: ({ nodeId, address }) => ({
        path: `v1/projects/${nodeId}/transactions`,
        version: 'v2',
        headers: {
          'x-offchain-address': address,
        },
      }),
      transformResponse: (returnValue: GetProjectWalletV2ResponseSchemaType) => returnValue.data,
      providesTags: (_result, _error, { nodeId, address }) => [{ type: 'ProjectRefundV2', id: `${nodeId}_${address}` }],
    }),
  }),
});

export const {
  useFetchProjectThemeV2Query,
  useFetchProjectGroupsV1Query,
  useFetchProjectWalletV1Query,
  useFetchProjectRefundV2Query,
  useFetchInventoriesV2Mutation,
  useUpdateReferralWalletV1Mutation,
  useUpdateReferralWalletV2Mutation,
} = projectApi;
